import { useCallback, useEffect, useState } from 'react';
import { marketplaceService, MarketplaceHardware, MarketplaceSoftware, MarketplaceConsulting } from '../services/marketplaceService';
import { partnerService } from '../services/partnerService';
import { detectVendorPreset, getComboKey } from './vendorPresets';
import { resolvedMicrosoftSkuId } from './microsoftLicenceLink';
import type { InclusionItem } from './inclusionItem';

export type ProductKind = 'software' | 'hardware' | 'consulting';

export interface ProductLite {
  id: string;
  name: string;
  kind: ProductKind;
  vendorOrBrand?: string;
  status?: 'active' | 'hidden';
  priceDisplay?: string;
  image?: string;
  tags?: string[];
  /** Domain for procurement routing - replaces orderTag */
  domain?: 'software' | 'computers' | 'networking' | 'mobile-hardware' | 'consulting' | 'telstra' | 'unified-collaboration';
  /** Source of the product: 'dickerdata' | 'ingram' | 'telstra-distributor' | 'manual' */
  source?: 'dickerdata' | 'ingram' | 'telstra-distributor' | 'manual';
  /** SKU for distributor-sourced products */
  sku?: string;
  /** Vendor part number from the distributor feed. Ingram calls this VPN. */
  vendorPartNumber?: string;
  /** Stock on hand for distributor products */
  soh?: number;
  /** Last sync timestamp for distributor products */
  lastSyncedAt?: string;
  /** Buy price from distributor (cost price) */
  buyPrice?: number;
  /** Sell price (what customer sees) */
  sellPrice?: number;
  /** Markup percentage applied (if using markup pricing) */
  markupPercent?: number;
  /** RRP from distributor for reference */
  rrpExTax?: number;
  /** Featured flag - surfaces product in Featured page for customers */
  featured?: boolean;
  /** Pricing type: perUser (subscription) or lumpSum (one-time) */
  pricingType?: 'perUser' | 'lumpSum';
  /** Contract term in months */
  term?: number;
  /** Billing cycle for subscription pricing */
  billingCycle?: 'monthly' | 'annual';
  /** For Telstra domain: mobile_voice or mobile_broadband */
  telstraProductType?: 'mobile_voice' | 'mobile_broadband';
  /** For mobile-hardware domain: device eligibility for plan attachment (DRO) */
  deviceEligibility?: 'outright_only' | 'mobile_voice' | 'mobile_broadband';
  /** Product colour (optional) */
  colour?: string;
  /** Product capacity e.g. 256GB (optional) */
  capacity?: string;
  /** Available colour options for mobile-hardware products (max 5). Customers select at checkout. */
  availableColours?: string[];
  /** Product description */
  description?: string;
  /** Custom questions for consulting products (max 5) */
  customQuestions?: Array<{
    id: string;
    label: string;
    required: boolean;
  }>;
  /** Plan inclusions/features for Telstra and software products (legacy string[] or rich InclusionItem[]). */
  planInclusions?: string[] | InclusionItem[];
  /** Features for computers and unified-collaboration products (legacy string[] or rich InclusionItem[]). */
  features?: string[] | InclusionItem[];
  /** Display order for sorting products (lower numbers appear first) */
  displayOrder?: number;
  /** Sub-category for filtering (e.g., Laptop/Desktop for computers, Router/Firewall for networking) */
  subCategory?: string;
  /** Vendor preset ID (e.g. 'microsoft') — drives term/billing constraints */
  vendorPreset?: string;
  /** Per billing/commitment combination pricing (keyed by "billingCycle_term") */
  termPricing?: Record<string, number>;
  /** Graph `subscribedSku.skuId` of the Microsoft licence a Microsoft plan sells. */
  microsoftSkuId?: string;
  /**
   * Canonical attributes from the per-SKU enrichment blob. Server-hydrated
   * for distributor-sourced hardware (Dicker / Ingram). Null for manual
   * products. Used by the customer-view to render normalised features.
   */
  enrichmentAttributes?: Record<string, unknown>;
}

export interface CatalogueLite {
  snapshotId: string;
  name: string;
  status: 'draft' | 'published';
  itemCount: number;
  notes?: string;
  createdAtUtc?: string;
}

export interface CustomerLite {
  id: string;
  name: string;
}

// Helper function to format prices with commas
const formatPrice = (price: number): string => {
  return price.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Track if a load is already in progress to prevent duplicate API calls
let loadInProgress = false;
let pendingForceReload = false;
let lastLoadTime = 0;
const MIN_LOAD_INTERVAL = 2000; // Minimum 2 seconds between reloads

// ============================================================================
// MARKETPLACE CACHE - Persist data across component remounts
// ============================================================================
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
interface MarketplaceCacheData {
  products: ProductLite[];
  catalogues: CatalogueLite[];
  customers: CustomerLite[];
  timestamp: number;
}

let marketplaceCache: MarketplaceCacheData | null = null;
let cacheInvalidated = false;

function isCacheValid(): boolean {
  if (!marketplaceCache || cacheInvalidated) return false;
  return Date.now() - marketplaceCache.timestamp < CACHE_TTL;
}

function setCacheData(products: ProductLite[], catalogues: CatalogueLite[], customers: CustomerLite[]): void {
  marketplaceCache = {
    products,
    catalogues,
    customers,
    timestamp: Date.now()
  };
  cacheInvalidated = false;
}

/**
 * Invalidate the marketplace cache. Call this when:
 * - A product/catalogue is created, updated, or deleted
 * - SignalR notifies of external changes
 */
export function invalidateMarketplaceCache(): void {
  cacheInvalidated = true;
}

export function useMarketplaceState() {
  const [products, setProducts] = useState<ProductLite[]>(marketplaceCache?.products || []);
  const [catalogues, setCatalogues] = useState<CatalogueLite[]>(marketplaceCache?.catalogues || []);
  const [customers, setCustomers] = useState<CustomerLite[]>(marketplaceCache?.customers || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (forceReload: boolean = false) => {
    // Prevent duplicate loads and rate limiting
    const now = Date.now();
    if (loadInProgress) {
      if (forceReload) pendingForceReload = true;
      return;
    }
    
    // Use cached data if available and not forced
    if (!forceReload && isCacheValid()) {
      if (marketplaceCache) {
        setProducts(marketplaceCache.products);
        setCatalogues(marketplaceCache.catalogues);
        setCustomers(marketplaceCache.customers);
      }
      return;
    }
    
    if (!forceReload && (now - lastLoadTime) < MIN_LOAD_INTERVAL) {
      return;
    }
    
    loadInProgress = true;
    lastLoadTime = now;
    setLoading(true);
    setError(null);
    try {
      // Load all catalogues
      const rawCats = await marketplaceService.listPartnerSnapshotsBlob();
      
      // Find all "All Products" catalogues
      const allProductsCats = (rawCats || []).filter(c => c.name === 'All Products');
      
      // Pick the one with most items, or the most recently created if all have same count
      let allProductsCat = allProductsCats.length > 0
        ? allProductsCats.reduce((best, current) => {
            // Prefer catalogue with more items
            if (current.itemCount > best.itemCount) return current;
            // If same count, prefer newer one (later createdAtUtc)
            if (current.itemCount === best.itemCount && current.createdAtUtc > best.createdAtUtc) return current;
            return best;
          }, allProductsCats[0])
        : null;
      
      // Delete duplicate "All Products" catalogues (keep only the best one)
      // This prevents the DistributorSyncFunction from overwriting customer assignments
      const duplicatesToDelete = allProductsCats.filter(c => 
        allProductsCat && c.snapshotId !== allProductsCat.snapshotId
      );
      if (duplicatesToDelete.length > 0) {
        for (const dup of duplicatesToDelete) {
          try {
            await marketplaceService.deletePartnerSnapshotBlob(dup.snapshotId);
          } catch (err) {
            // Ignore cleanup failures for duplicate catalogues
          }
        }
      }
      
      if (!allProductsCat) {
        // Create "All Products" catalogue if it doesn't exist
        await marketplaceService.createPartnerSnapshotBlob({
          name: 'All Products',
          notes: 'Default catalogue containing all your products',
          software: [],
          hardware: [],
          consulting: []
        });
        
        // Reload to get the new catalogue
        const updatedCats = await marketplaceService.listPartnerSnapshotsBlob();
        allProductsCat = (updatedCats || []).find(c => c.name === 'All Products') || null;
        
        if (allProductsCat) {
          // Use updated list for further processing
          rawCats.splice(0, 0, allProductsCat);
        }
      }

      // Filter out:
      // 1. Duplicate "All Products" catalogues - keep only the one with most items
      // 2. "No Catalogue" - this is a system catalogue that shouldn't be visible to users
      const deduplicatedCats = (rawCats || []).filter(c => {
        // Always hide "No Catalogue"
        if (c.name === 'No Catalogue') return false;
        // Keep only the selected "All Products" catalogue, filter out duplicates
        if (c.name === 'All Products' && c.snapshotId !== allProductsCat?.snapshotId) return false;
        return true;
      });

      // Organize catalogues with "All Products" first
      const sortedCats = deduplicatedCats.sort((a, b) => {
        if (a.name === 'All Products') return -1;
        if (b.name === 'All Products') return 1;
        return 0;
      });

      const loadedCatalogues: CatalogueLite[] = sortedCats.map((c) => ({
        snapshotId: c.snapshotId,
        name: c.name,
        status: 'published',
        itemCount: c.itemCount,
        notes: (c as any).notes,
        createdAtUtc: c.createdAtUtc,
      }));
      setCatalogues(loadedCatalogues);

      // Load products from "All Products" catalogue
      let loadedProducts: ProductLite[] = [];
      if (allProductsCat) {
        const doc = await marketplaceService.getPartnerSnapshotBlob(allProductsCat.snapshotId);
        const list: ProductLite[] = [];
        
        (doc.software || []).forEach((s: MarketplaceSoftware) => {
          const preset = detectVendorPreset(s.vendor || (s as any).brand);
          const validTerms = preset ? preset.validCombinations.map(c => c.term) : null;
          const validBilling = preset ? preset.validCombinations.map(c => c.billingCycle) : null;
          const correctedTerm = preset && validTerms && !validTerms.includes(s.term) ? preset.defaultTerm : s.term;
          const correctedBilling = preset && validBilling && !validBilling.includes(s.billingCycle as any) ? preset.defaultBillingCycle : s.billingCycle;

          let termPricing = (s as any).termPricing as Record<string, number> | undefined;
          if (preset && !termPricing) {
            const basePrice = typeof s.price === 'number' ? s.price : 0;
            termPricing = {};
            for (const combo of preset.validCombinations) {
              termPricing[getComboKey(combo.billingCycle, combo.term)] = basePrice;
            }
          }

          list.push({
            id: s.id,
            name: s.name,
            kind: 'software',
            vendorOrBrand: preset?.label || s.vendor || (s as any).brand,
            status: s.enabled === false ? 'hidden' : 'active',
            priceDisplay: (() => {
              if (preset && termPricing) {
                const m2mKey = getComboKey('monthly', 0);
                const m2mPrice = termPricing[m2mKey];
                if (typeof m2mPrice === 'number' && m2mPrice > 0) {
                  return `$${formatPrice(m2mPrice)}/user (M2M)`;
                }
              }
              if (typeof s.price === 'number' && s.price > 0) {
                return s.pricingType === 'lumpSum' ? `$${formatPrice(s.price)}` : `$${formatPrice(s.price)}/user`;
              }
              return undefined;
            })(),
            image: s.image,
            tags: ((): string[] => {
              const arr: string[] = [];
              arr.push((s as any).category || 'General');
              return arr;
            })(),
            domain: ((s as any).domain || 'software').toLowerCase(),
            source: (s as any).source || 'manual',
            sku: (s as any).sku,
            vendorPartNumber: (s as any).vendorPartNumber,
            soh: (s as any).soh,
            buyPrice: (s as any).buyPrice,
            sellPrice: s.price,
            markupPercent: (s as any).markupPercent,
            rrpExTax: (s as any).rrpExTax,
            featured: Boolean(s.popular),
            pricingType: s.pricingType || 'perUser',
            term: correctedTerm,
            billingCycle: correctedBilling,
            description: s.description,
            displayOrder: (s as any).displayOrder,
            subCategory: (s as any).subCategory,
            vendorPreset: preset?.id || (s as any).vendorPreset,
            termPricing,
            microsoftSkuId: resolvedMicrosoftSkuId({
              kind: 'software',
              domain: ((s as any).domain || 'software').toLowerCase(),
              name: s.name,
              vendorOrBrand: preset?.label || s.vendor || (s as any).brand,
              vendorPreset: preset?.id || (s as any).vendorPreset,
              microsoftSkuId: s.microsoftSkuId,
            }),
          });
        });
        
        (doc.hardware || []).forEach((h: MarketplaceHardware) => {
          const domain = ((h as any).domain || h.category || 'computers').toLowerCase();
          const software = domain === 'software';
          const preset = software ? ((h as any).vendorPreset ? { id: (h as any).vendorPreset, label: h.brand } : detectVendorPreset(h.brand)) : null;
          list.push({
            id: h.id,
            name: h.name,
            kind: software ? 'software' : 'hardware',
            vendorOrBrand: preset?.label || h.brand,
            status: h.enabled === false ? 'hidden' : 'active',
            priceDisplay: typeof h.price === 'number' ? `$${formatPrice(h.price)}` : undefined,
            image: h.image,
            tags: (() => {
              const arr: string[] = [];
              arr.push(h.category);
              return arr;
            })(),
            domain,
            source: (h as any).source || 'manual',
            sku: (h as any).sku,
            vendorPartNumber: (h as any).vendorPartNumber,
            soh: (h as any).soh,
            lastSyncedAt: (h as any).lastSyncedAt,
            buyPrice: (h as any).buyPrice,
            sellPrice: h.price,
            markupPercent: (h as any).markupPercent,
            rrpExTax: (h as any).rrpExTax,
            featured: Boolean(h.popular),
            pricingType: (h as any).pricingType || 'lumpSum',
            term: (h as any).term,
            billingCycle: (h as any).billingCycle,
            // Telstra-specific fields
            telstraProductType: (h as any).telstraProductType,
            // Plan inclusions for Telstra products
            planInclusions: (h as any).planInclusions,
            // Mobile Hardware device eligibility for DRO
            deviceEligibility: (h as any).deviceEligibility,
            // Optional product attributes
            colour: (h as any).colour,
            capacity: (h as any).capacity,
            // Available colours for mobile-hardware
            availableColours: (h as any).availableColours,
            // Features for computers domain
            features: (h as any).features,
            description: h.description,
            // Display order for sorting within domain
            displayOrder: (h as any).displayOrder,
            // Sub-category for filtering (e.g., Laptop/Desktop for computers)
            subCategory: (h as any).subCategory,
            vendorPreset: preset?.id,
            termPricing: (h as any).termPricing,
            microsoftSkuId: resolvedMicrosoftSkuId({
              kind: software ? 'software' : 'hardware',
              domain,
              name: h.name,
              vendorOrBrand: preset?.label || h.brand,
              vendorPreset: preset?.id,
              microsoftSkuId: (h as any).microsoftSkuId,
            }),
            // Server-hydrated canonical attributes (distributor SKUs only)
            enrichmentAttributes: (h as any).enrichmentAttributes,
          });
        });
        
        ((doc as any).consulting || []).forEach((c: MarketplaceConsulting) => list.push({
          id: c.id,
          name: c.name,
          kind: 'consulting',
          vendorOrBrand: c.brand,
          status: c.enabled === false ? 'hidden' : 'active',
          priceDisplay: typeof (c as any).price === 'number' ? `$${formatPrice((c as any).price)}` : undefined,
          image: (c as any).image,
          tags: (() => {
            const arr: string[] = [];
            arr.push((c as any).category || 'Consulting');
            return arr;
          })(),
          // Normalize domain to lowercase for consistent comparisons
          domain: ((c as any).domain || 'consulting').toLowerCase(),
          featured: Boolean((c as any).popular),
          pricingType: (c as any).pricingType || 'lumpSum',
          term: (c as any).term,
          billingCycle: (c as any).billingCycle,
          description: (c as any).description,
          customQuestions: c.customQuestions,
          // Display order for sorting within domain
          displayOrder: (c as any).displayOrder,
        }));
        
        setProducts(list);
        loadedProducts = list;
      } else {
        setProducts([]);
        loadedProducts = [];
      }

      // Customers (lite)
      let loadedCustomers: CustomerLite[] = [];
      try {
        const ctx = await partnerService.getPartnerAuthContext();
        const partnerId = (ctx.data as any)?.partnerId || (ctx.data as any)?.id || 'self';
        const resp = await partnerService.getPartnerCustomers(partnerId);
        let custs = (resp.data || []).map((c) => ({ id: c.customerId, name: c.customerName }));
        if ((!custs || custs.length === 0) && (ctx.data as any)?.managedCustomers) {
          const managed = ((ctx.data as any).managedCustomers as any[]);
          custs = managed.map((c: any) => ({ id: c.customerId, name: c.customerName }));
        }
        setCustomers(custs || []);
        loadedCustomers = custs || [];
      } catch (e) {
        // Fallback to managedCustomers inside auth context if available; else empty list
        try {
          const ctx2 = await partnerService.getPartnerAuthContext();
          const managed = ((ctx2.data as any)?.managedCustomers || []) as any[];
          const custs = managed.map((c: any) => ({ id: c.customerId, name: c.customerName }));
          setCustomers(custs);
          loadedCustomers = custs;
        } catch {
          setCustomers([]);
          loadedCustomers = [];
        }
      }
      
      // Save to cache
      setCacheData(loadedProducts, loadedCatalogues, loadedCustomers);
    } catch (e: any) {
      setError(e?.message || 'Failed to load marketplace state');
    } finally {
      setLoading(false);
      loadInProgress = false;
      if (pendingForceReload) {
        pendingForceReload = false;
        void load(true);
      }
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const searchAll = useCallback((query: string) => {
    const q = query.trim().toLowerCase();
    if (!q) return { products: [], catalogues: [], customers: [] };
    const match = (s: string | undefined) => (s || '').toLowerCase().includes(q);
    return {
      products: products.filter((p) => match(p.name) || match(p.vendorOrBrand)),
      catalogues: catalogues.filter((c) => match(c.name) || match(c.notes)),
      customers: customers.filter((c) => match(c.name)),
    };
  }, [products, catalogues, customers]);

  return {
    loading,
    error,
    products,
    setProducts,
    catalogues,
    setCatalogues,
    customers,
    setCustomers,
    searchAll,
    reload: load,
  } as const;
}

export default useMarketplaceState;
